import { useQuotaRequestMore } from '@/hooks'
import { formatPeriodEnd, useEntitlements } from '@/lib'
import { cn } from '@/lib/utils'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * Displays campaign quota as a subtle banner.
 * Hidden when the limit is unlimited or no quota data exists.
 */
export function CampaignQuotaDisplay() {
	const { campaignQuota, isLoading } = useEntitlements()
	const { t } = useTranslation('common')
	const { request, hasSent, isDisabled, isError } = useQuotaRequestMore(campaignQuota.periodEnd)

	if (isLoading || campaignQuota.limit === null) {
		return null
	}

	if (!campaignQuota.isNearLimit && !campaignQuota.isExhausted) {
		return null
	}

	const remaining = Math.max(campaignQuota.limit - campaignQuota.usage, 0)
	const formattedPeriodEnd = formatPeriodEnd(campaignQuota.periodEnd)

	return (
		<div
			className={cn(
				'mb-4 flex items-start gap-3 rounded-xl border px-4 py-3',
				campaignQuota.isExhausted
					? 'border-amber-200 bg-amber-50 text-amber-900'
					: campaignQuota.isNearLimit
					? 'border-amber-100 bg-amber-50/70 text-amber-900'
					: 'border-grey-200 bg-grey-50 text-grey-700',
			)}
		>
			<InfoIcon className="mt-0.5 size-4 shrink-0" />
			<div className="min-w-0">
				<p className="text-sm font-medium">{t('campaignQuota.title')}</p>
				<div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
					<span>{t('campaignQuota.summary', { used: campaignQuota.usage, limit: campaignQuota.limit })}</span>
					<span className="text-current/70">
						{campaignQuota.isExhausted
							? formattedPeriodEnd
								? t('campaignQuota.exhaustedWithDate', { date: formattedPeriodEnd })
								: t('campaignQuota.exhausted')
							: t('campaignQuota.remaining', { count: remaining })}
					</span>

					{(campaignQuota.isNearLimit || campaignQuota.isExhausted) && (
						<button
							type="button"
							onClick={request}
							disabled={isDisabled}
							className={cn(
								'cursor-pointer underline underline-offset-2 hover:no-underline disabled:opacity-50 disabled:no-underline disabled:cursor-default',
								isError ? 'text-red-700' : hasSent ? 'text-green-700' : 'text-current',
							)}
						>
							{isError
								? t('campaignQuota.requestFailed')
								: hasSent
								? t('campaignQuota.requestSent')
								: t('campaignQuota.requestMore')}
						</button>
					)}
				</div>
			</div>
		</div>
	)
}
